import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useClerkAuth';
import TopNavBar from '@/components/TopNavBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  BookOpen,
  PlusCircle,
  Users,
  BarChart3,
  Settings,
  FileText,
  TrendingUp,
  Clock,
  Star,
  ArrowRight,
} from 'lucide-react';
import { fetchArticles } from '@/services/articles';

const MainDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, canCreateArticle, canEditArticle, canManageRoles } = useAuth();
  const [stats, setStats] = useState({
    totalArticles: 0,
    recentArticles: 0,
    isLoading: true,
  });

  const loadStats = useCallback(async () => {
    try {
      if (canEditArticle()) {
        const articles = await fetchArticles();
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7);

        const recentArticles = articles.filter(
          article => new Date(article.createdAt) > lastWeek
        ).length;

        setStats({
          totalArticles: articles.length,
          recentArticles,
          isLoading: false,
        });
      } else {
        setStats({ totalArticles: 0, recentArticles: 0, isLoading: false });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ totalArticles: 0, recentArticles: 0, isLoading: false });
    }
  }, [canEditArticle]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const navigationCards = [
    // Reader features (all users)
    {
      title: t('dashboard.readingProgress'),
      description: t('dashboard.readingProgressDesc'),
      icon: BarChart3,
      path: '/progress',
      color: 'bg-blue-500',
      available: true,
    },
    {
      title: t('dashboard.practiceQuestions'),
      description: t('dashboard.practiceQuestionsDesc'),
      icon: FileText,
      path: '/questions',
      color: 'bg-green-500',
      available: true,
    },

    // Editor features
    {
      title: t('dashboard.createArticleCard'),
      description: t('dashboard.createArticleDesc'),
      icon: PlusCircle,
      path: '/create-article',
      color: 'bg-purple-500',
      available: canCreateArticle(),
    },
    {
      title: t('dashboard.manageArticles'),
      description: t('dashboard.manageArticlesDesc'),
      icon: BookOpen,
      path: '/manage-articles',
      color: 'bg-orange-500',
      available: canEditArticle(),
    },

    // Admin features
    {
      title: t('dashboard.userManagement'),
      description: t('dashboard.userManagementDesc'),
      icon: Users,
      path: '/manage-users',
      color: 'bg-red-500',
      available: canManageRoles(),
    },

    // General settings
    {
      title: t('dashboard.settingsCard'),
      description: t('dashboard.settingsDesc'),
      icon: Settings,
      path: '/settings',
      color: 'bg-gray-500',
      available: true,
    },
  ].filter(card => card.available);

  const quickStats = [
    {
      title: t('dashboard.totalArticles'),
      value: stats.totalArticles,
      icon: BookOpen,
      description: t('dashboard.articlesCreated'),
      show: canEditArticle(),
    },
    {
      title: t('dashboard.recentArticles'),
      value: stats.recentArticles,
      icon: TrendingUp,
      description: t('dashboard.createdThisWeek'),
      show: canEditArticle(),
    },
    {
      title: t('dashboard.learningStreak'),
      value: '7 days', // This would come from user progress
      icon: Star,
      description: t('dashboard.currentStreak'),
      show: true,
    },
    {
      title: t('dashboard.readingTime'),
      value: '2.5h', // This would come from user progress
      icon: Clock,
      description: t('dashboard.thisWeek'),
      show: true,
    },
  ].filter(stat => stat.show);

  return (
    <>
      <TopNavBar
        currentPage="dashboard"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground mb-6">
              <h1 className="text-3xl font-bold mb-2">
                {t('dashboard.welcome', 'Welcome back')},{' '}
                {user?.firstName || 'Learner'}!
              </h1>
              <p className="text-primary-foreground/90">
                {t(
                  'dashboard.subtitle',
                  'Continue your German learning journey from where you left off.'
                )}
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {quickStats.map((stat, index) => (
              <Card
                key={index}
                className="bg-white/95 backdrop-blur-sm border-0 shadow-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </p>
                      <p className="text-2xl font-bold">
                        {stats.isLoading ? '...' : stat.value}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {stat.description}
                      </p>
                    </div>
                    <stat.icon className="h-8 w-8 text-learning-primary" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Navigation Cards */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-6">
              {t('dashboard.quickAccess', 'Quick Access')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {navigationCards.map((card, index) => (
                <Card
                  key={index}
                  className="group hover:shadow-lg transition-all duration-300 cursor-pointer bg-white/95 backdrop-blur-sm border-0 shadow-card hover:shadow-elevated"
                  onClick={() => navigate(card.path)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-lg ${card.color} text-white`}
                        >
                          <card.icon className="h-6 w-6" />
                        </div>
                        <div>
                          <CardTitle className="text-lg group-hover:text-learning-primary transition-colors">
                            {card.title}
                          </CardTitle>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-learning-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <CardDescription className="text-muted-foreground">
                      {card.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Recent Activity Section - Placeholder for future implementation */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t('dashboard.recentActivity', 'Recent Activity')}
              </CardTitle>
              <CardDescription>
                {t(
                  'dashboard.activityDescription',
                  'Your latest learning activities and progress'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {t('dashboard.comingSoon', 'Activity tracking coming soon!')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default MainDashboard;
